import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/base/ArticleCard';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

const featuredArticles = articles.filter((a) => ['article-1', 'article-6', 'article-10'].includes(a.id));
const latestArticles = articles.slice(0, 6);
const hotArticles = [...articles].sort((a, b) => b.views - a.views).slice(0, 4);

const faqs = [
  {
    question: '欧美日韩一区平台主要提供什么内容？',
    answer: '欧美日韩一区是一个精选内容聚合平台，汇聚来自欧美、日本、韩国等地区的优质影视、音乐与文化内容资源。我们覆盖热门剧集、电影推荐、音乐专辑、文化资讯等多个领域，为您提供一站式的欧美日韩一区分类资源导航，帮助您快速发现最新、最优质的精选内容。',
  },
  {
    question: '欧美日韩一区的内容多久更新一次？',
    answer: '我们保持每日更新的频率，确保您能够第一时间获取欧美日韩一区最新资源。编辑团队持续追踪欧美、日本、韩国的最新影视上线、音乐发布与文化动态，热门推荐与分类导航内容实时刷新，让欧美日韩一区精选内容始终走在最前沿。',
  },
  {
    question: '欧美日韩一区平台的内容是否免费？',
    answer: '是的，欧美日韩一区平台上的所有精选内容导航与推荐文章均为免费提供。我们致力于打造一个开放、高质量的欧美日韩一区内容发现平台，用户无需付费即可浏览所有分类导航、热门推荐与精选文章。部分外部链接指向的内容可能需要对应平台的会员订阅。',
  },
  {
    question: '如何快速找到我感兴趣的欧美日韩一区内容？',
    answer: '您可以通过首页的分类导航快速进入对应区域（欧美、日本、韩国）和类型（电影、剧集、音乐）的分类页面。每个分类页面都提供了Tab切换功能，方便您在不同类别之间灵活跳转。此外，首页的热门推荐区域也展示了当前最受关注的欧美日韩一区精选内容。',
  },
  {
    question: '欧美日韩一区覆盖哪些国家和地区的内容？',
    answer: '欧美日韩一区主要覆盖三大区域：欧美地区（包括美国、英国、欧洲各国）、日本、韩国。每个区域下分为电影、剧集、音乐三大类型，共九个细分分类，全方位满足您对欧美日韩一区内容的探索需求。',
  },
];

export default function Home() {
  const [selectedFaq, setSelectedFaq] = useState<number | null>(null);

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '欧美日韩一区精选内容聚合平台',
    url: 'https://www.oumeirihan.com',
    description: '汇聚全球优质影视、音乐、文化内容的精选聚合平台',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://www.oumeirihan.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
    inLanguage: 'zh-CN',
  };

  return (
    <>
      <SchemaOrg type="WebSite" data={websiteSchema} />
      <SchemaOrg
        type="FAQPage"
        data={{
          mainEntity: faqs.map((faq) => ({
            '@type': 'Question',
            name: faq.question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: faq.answer,
            },
          })),
        }}
      />

      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative w-full min-h-[520px] md:min-h-[620px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20cultural%20media%20collage%20with%20film%20reel%20and%20music%20notes%20silhouettes%2C%20warm%20golden%20and%20amber%20lighting%2C%20artistic%20abstract%20composition%2C%20clean%20elegant%20atmosphere%2C%20soft%20gradient%20background%2C%20no%20text%20no%20letters&width=1600&height=700&seq=home-hero-2026&orientation=landscape"
            alt="欧美日韩一区精选内容聚合平台"
            className="absolute inset-0 w-full h-full object-cover object-top"
            title="欧美日韩一区精选内容聚合平台"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/20 to-black/45"></div>
          <div className="relative z-10 w-full max-w-4xl mx-auto px-4 md:px-6 text-center">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold font-heading text-white leading-tight mb-6">
              欧美日韩一区精选内容聚合平台
            </h1>
            <p className="text-base md:text-lg text-white/85 max-w-2xl mx-auto leading-relaxed mb-8">
              汇聚全球优质影视、音乐、文化内容，为您提供<strong>欧美日韩一区</strong>最全分类资源导航。覆盖热门剧集、电影、音乐专辑，一站式发现<strong>欧美日韩一区</strong>最新精选内容，持续更新，体验无界文化娱乐。
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/category/all"
                className="px-8 py-3 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors duration-200 shadow-lg shadow-primary-500/25 whitespace-nowrap cursor-pointer"
              >
                探索分类导航
                <i className="ri-arrow-right-line ml-2"></i>
              </Link>
              <Link
                to="/about"
                className="px-8 py-3 rounded-full bg-white/15 backdrop-blur-sm text-white font-medium text-sm hover:bg-white/25 transition-colors duration-200 border border-white/20 whitespace-nowrap cursor-pointer"
              >
                了解平台
              </Link>
            </div>
          </div>
        </section>

        {/* Category Quick Nav */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-3">
                <strong>欧美日韩一区</strong>分类导航
              </h2>
              <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
                覆盖欧美、日本、韩国三大区域，电影、剧集、音乐三大类型，九大分类一网打尽
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className="group flex items-start gap-4 p-5 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-300 hover:bg-background-100 transition-all duration-200 cursor-pointer"
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 flex-shrink-0 group-hover:bg-primary-500 group-hover:text-background-50 transition-colors duration-200">
                    <i className={`${cat.icon} text-xl`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors duration-200 whitespace-nowrap">
                      {cat.name}
                    </h3>
                    <p className="text-sm text-foreground-500 mt-1 line-clamp-2 leading-relaxed">
                      {cat.description}
                    </p>
                    <span className="inline-flex items-center text-xs text-primary-500 mt-2 font-medium">
                      浏览内容 <i className="ri-arrow-right-line ml-1"></i>
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Content */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-2">
                  <strong>欧美日韩一区</strong>精选推荐
                </h2>
                <p className="text-sm text-foreground-500">编辑团队精心挑选的优质内容</p>
              </div>
              <Link
                to="/category/all"
                className="hidden sm:flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors whitespace-nowrap"
              >
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {featuredArticles.map((article) => (
                <ArticleCard key={article.id} article={article} variant="featured" />
              ))}
            </div>
          </div>
        </section>

        {/* Latest Content */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-end justify-between mb-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-2">
                  <strong>欧美日韩一区</strong>最新资源
                </h2>
                <p className="text-sm text-foreground-500">持续更新，追踪全球文化娱乐前沿</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {latestArticles.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-12 bg-accent-500">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              {[
                { value: '9', label: '内容分类' },
                { value: '500+', label: '精选文章' },
                { value: '120万', label: '月访问量' },
                { value: '99.7%', label: '内容好评率' },
              ].map((stat) => (
                <div key={stat.label}>
                  <div className="text-2xl md:text-3xl font-bold text-background-50 font-heading">{stat.value}</div>
                  <div className="text-sm text-background-50/80 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Hot / Sidebar */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="mb-10">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-2">
                <strong>欧美日韩一区</strong>热门推荐
              </h2>
              <p className="text-sm text-foreground-500">当前最受关注的精选内容</p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {hotArticles.map((article) => (
                <ArticleCard key={article.id} article={article} variant="compact" />
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-3">
                常见问题
              </h2>
              <p className="text-sm text-foreground-500">关于<strong>欧美日韩一区</strong>平台，您可能想了解的</p>
            </div>
            <div className="space-y-3">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className="rounded-xl border border-background-200/70 bg-background-50 overflow-hidden"
                >
                  <button
                    className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer"
                    onClick={() => setSelectedFaq(selectedFaq === index ? null : index)}
                  >
                    <h3 className="text-sm md:text-base font-medium text-foreground-900 pr-4">{faq.question}</h3>
                    <div className={`w-6 h-6 flex items-center justify-center flex-shrink-0 transition-transform duration-200 ${selectedFaq === index ? 'rotate-180' : ''}`}>
                      <i className="ri-arrow-down-s-line text-foreground-500"></i>
                    </div>
                  </button>
                  {selectedFaq === index && (
                    <div className="px-5 pb-4">
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20amber%20and%20golden%20abstract%20gradient%20with%20soft%20light%20bokeh%20effect%2C%20minimalist%20elegant%20background%2C%20smooth%20texture%2C%20clean%20aesthetic%2C%20no%20text%20no%20letters&width=1600&height=500&seq=cta-bg-2026&orientation=landscape"
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
            title=""
          />
          <div className="absolute inset-0 bg-primary-900/80"></div>
          <div className="relative z-10 text-center px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl font-bold font-heading text-background-50 mb-4">
              加入<strong>欧美日韩一区</strong>内容社区
            </h2>
            <p className="text-sm md:text-base text-background-50/80 max-w-lg mx-auto mb-8">
              订阅我们的 Newsletter，第一时间获取欧美日韩一区最新精选内容更新与独家推荐
            </p>
            <Link
              to="/category/all"
              className="inline-flex items-center px-8 py-3 rounded-full bg-accent-500 text-background-50 font-medium text-sm hover:bg-accent-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              立即探索 <i className="ri-arrow-right-line ml-2"></i>
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}